import React from 'react';

import { Button, Group, LoadingOverlay, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import SubScript from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { AxiosError, AxiosResponse } from 'axios';
import KiloAlert from 'components/KiloAlert';
import DOMPurify from 'dompurify';
import { UseMutationResult, UseQueryResult } from 'react-query';
import { ErrorMessage, Invoice, InvoiceRetrieve, InvoiceUpdateNotes } from 'types';
import notificationHandler from 'utils/notifications';

import { InvoiceDrawerState } from '../types';

type FormValues = {
  invoiceId: string;
  notes: string;
};

interface InvoiceNotesTabProps {
  state: InvoiceDrawerState;
  setState: React.Dispatch<React.SetStateAction<InvoiceDrawerState>>;
  invoiceId: string;
  invoiceQuery: UseQueryResult<{
    data: InvoiceRetrieve;
  }>;
  updateInvoiceNotesMutation: UseMutationResult<
    AxiosResponse<Invoice>,
    AxiosError<ErrorMessage>,
    InvoiceUpdateNotes
  >;
}

const InvoiceNotesTab = ({
  state,
  setState,
  invoiceId,
  invoiceQuery,
  updateInvoiceNotesMutation,
}: InvoiceNotesTabProps): JSX.Element => {
  const { errorNotification, successNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      invoiceId: invoiceId,
      notes: '',
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      Superscript as AnyExtension,
      SubScript as AnyExtension,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: form.getInputProps('notes').value,
    onUpdate: ({ editor: _editor }) => {
      const cleanHtml = DOMPurify.sanitize(_editor.getHTML());
      form.setFieldValue('notes', cleanHtml);
    },
    editable: state === 'Edit Notes',
  });

  const handleSubmit = (values: FormValues): void => {
    updateInvoiceNotesMutation.mutate(
      {
        invoiceId: values.invoiceId,
        notes: values.notes,
      },
      {
        onSuccess: () => {
          successNotification('Invoice updated successfully');
          setState('View');
        },
        onError: e => {
          errorNotification('Invoice update failed', e.response?.data.message);
        },
      },
    );
  };

  useShallowEffect(() => {
    if (editor && invoiceQuery.isSuccess && invoiceQuery.data?.data) {
      const content = invoiceQuery.data.data.notes || '';
      form.setFieldValue('notes', content);
      editor.commands.setContent(content);
      if (state === 'View') {
        editor.setEditable(false);
      } else {
        editor.setEditable(true);
      }
    }
  }, [editor, invoiceQuery.data?.data, state]);

  if (invoiceQuery.isError) {
    return (
      <KiloAlert title='Error' variantStyle='error' color='red'>
        Something went wrong while fetching data!
      </KiloAlert>
    );
  }

  if (invoiceQuery.isLoading) {
    return <LoadingOverlay visible />;
  }

  return (
    <Stack h='100%'>
      {state === 'View' && (
        <Group position='right'>
          <Button size='sm' onClick={() => setState('Edit Notes')}>
            Edit
          </Button>
        </Group>
      )}
      <form
        id={'invoice-notes'}
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ flexGrow: 1 }}
        onClick={() => editor?.commands.focus()}
      >
        <RichTextEditor editor={editor} h='100%'>
          <RichTextEditor.Toolbar sticky display={state === 'Edit Notes' ? 'default' : 'none'}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Superscript />
              <RichTextEditor.Subscript />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
          <RichTextEditor.Content />
        </RichTextEditor>
      </form>
    </Stack>
  );
};
export default InvoiceNotesTab;
